import { useEffect, useState, useRef, useContext, useCallback } from "react"; 
import Message from "./Message";   
import socket from "../services/socket";
import { useAuth } from "../../../../context/AuthContext";
import edamsApi from "../../../../models/edamsModel";
// import { useSignal } from "@preact/signals-react"; // Temporarily remove for testing
// import { useSignals } from "@preact/signals-react/runtime"; // Temporarily remove for testing
import { PaperAirplaneIcon, PaperClipIcon, XMarkIcon, MagnifyingGlassIcon, HomeIcon, PencilSquareIcon, InformationCircleIcon, CreditCardIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline"; 
import { ChatContext } from "../context/ChatContext"; 
import SideChatAccount from "./SideChatAccount";
// import Resizer from "react-image-file-resizer"; // Replaced with custom resizer
import IconButton from "./IconButton";
import CustomerSearchModal from "./CustomerSearchModal"; // Import the new modal
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
// Import the notification sound file directly
// import notificationSound from "../assets/notification.mp3";
import AsyncLocalStorage from '@createnextapp/async-local-storage';

const MessagesDashboard = () => {
    // useSignals(); // Temporarily remove for testing
    const { data, dispatch } = useContext(ChatContext); 
    const { user } = useAuth(); // Get user from auth context
    const [currentChat, setCurrentChat] = useState(data.currentChat);
    // const messages = useSignal([]); // Temporarily replace with useState
    const [messages, setMessages] = useState([]); 
    const [text, setText] = useState("");
    const inputFile = useRef(null); 
    const [file, setFile] = useState(); 
    const [isTyping, setIsTyping] = useState(false); 
    const [preview, setPreview] = useState(false);
    const [department, setDepartment] = useState(1);
    // const messageSound = useRef(new Audio(notificationSound));  
    const [accountNo, setAccountNo] = useState(data.accountNo);
    const [chatUser, setChatUser] = useState(data.user);
    const [customer, setCustomer] = useState(data.customer);
    // Use navigation directly from context instead of local state
    const navigation = data.navigation;
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false); // State for modal visibility
    // Hide sidebar by default when a chat is selected, show it otherwise
    const [isSidebarVisible, setIsSidebarVisible] = useState(!data.currentChat); // State for sidebar visibility
    
    // Check if customer data is available
    const hasCustomerData = !data.isAccountDataLoading && 
                           data.accountData && 
                           data.accountData.account && 
                           Object.keys(data.accountData.account).length > 0;
    
    // Use ref to keep current chat and user up to date in event handlers (avoid stale closures)
    const currentChatRef = useRef(data.currentChat);
    const userRef = useRef(user);

    // Update refs whenever values change
    useEffect(() => {
      currentChatRef.current = data.currentChat;
    }, [data.currentChat]);

    useEffect(() => {
      userRef.current = user;
    }, [user]);

    // Custom image resizer function to replace react-image-file-resizer
    const resizeImage = (file, maxWidth = 600, maxHeight = 600, quality = 0.8) => {
      return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        img.onload = () => {
          // Calculate new dimensions
          let { width, height } = img;
          
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and resize image
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64
          const base64 = canvas.toDataURL('image/jpeg', quality);
          resolve(base64);
        };
        
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = URL.createObjectURL(file);
      });
    };

    // Effect for synchronizing non-socket related local state from context
    useEffect(() => {
    setCurrentChat(data.currentChat); 
    setAccountNo(data.accountNo);
    setChatUser(data.user);
    setCustomer(data.customer);
    // Auto-hide sidebar when a chat is selected (only trigger on chat change, not navigation/customer updates)
    if (data.currentChat) {
      setIsSidebarVisible(false);
      
      // Request messages for the selected channel
      if (data.currentChat.channel_id) {
        console.log(`📨 Chat selected, socket.connected=${socket.connected}, channel_id=${data.currentChat.channel_id}`);
        if (socket.connected) {
          // Get clientId and emit set-channel with proper format
          AsyncLocalStorage.getItem("silu-ClientId").then(clientId => {
            if (clientId) {
              console.log(`✅ Emitting set-channel: {clientId: ${clientId}, channelId: ${data.currentChat.channel_id}}`);
              socket.emit("set-channel", {clientId: clientId, channelId: data.currentChat.channel_id});
            } else {
              console.error("❌ No clientId found in AsyncLocalStorage");
            }
          });
        } else {
          console.warn(`⚠️ Socket not connected yet, cannot request messages for channel: ${data.currentChat.channel_id}`);
        }
      }
    }
    }, [data.currentChat, data.accountNo, data.user]);

    // Listen for new messages from context (sent from top-level Chat component)
    useEffect(() => {
      if (data.receivedMessage) {
        console.log("📨 MessagesDashboard: Received message from context:", data.receivedMessage);
        handleReceiveMessage(data.receivedMessage);
      }
    }, [data.receivedMessage]);

    // Define handleReceiveMessage before the useEffect above
    const handleReceiveMessage = (new_message) => {
      const currentChannelId = data.currentChat?.channel_id;
      
      console.log("📩 MessagesDashboard processing message:", new_message, "Current channel:", currentChannelId);
      
      if (!new_message || typeof new_message !== 'object') {
        console.warn("Received invalid new_message:", new_message);
        return;
      }

      // Use refs to get latest values (avoid stale closure)
      const currentUser = userRef.current;
      
      // Skip duplicate messages by checking if message with same ID already exists
      const isDuplicate = messages.some(msg => msg.id === new_message.id);
      if (isDuplicate) {
        console.log('⏭️ Skipping duplicate message (ID already exists):', new_message.id);
        return;
      }
      
      // Skip our own outgoing messages (already added optimistically when sending)
      const isOwnMessage = new_message.direction === 1 && 
                          currentUser && 
                          (new_message.userId === currentUser.user_id || 
                           new_message.userName === currentUser.user_full_name || 
                           new_message.name === currentUser.user_full_name);
      
      if (isOwnMessage) {
        console.log('⏭️ Skipping own message (already added optimistically)', {
          messageUserId: new_message.userId,
          currentUserId: currentUser.user_id,
          messageName: new_message.name,
          currentUserName: currentUser.user_full_name
        });
        return;
      }

      // Get message channel (handle both field names)
      const messageChannel = String(new_message.channel_id || new_message.channelId || '');
      
      console.log('📊 Channel comparison:', { 
        messageChannel, 
        currentChannelId, 
        match: messageChannel === String(currentChannelId)
      });

      // Only add message if it's for the currently active chat
      if (messageChannel === String(currentChannelId)) { 
        console.log("✅ Message is for current channel, adding to state");
        setMessages(prevMessages => Array.isArray(prevMessages) ? [...prevMessages, new_message] : [new_message]);
        
        // Mark as read if incoming message
        if (new_message.direction === 0) {
          socket.emit("read-message", new_message); 
        }
      } else {
        console.log("⚠️ Message not for current channel. Message channel:", messageChannel, "Current:", currentChannelId);
      }
    };

    useEffect(() => {   
    const currentChannelId = data.currentChat?.channel_id;

    // If there's no valid current channel, don't set up listeners.
    if (!currentChannelId || currentChannelId === 0) {
        setMessages([]); // Clear messages if no chat is active
        //console.log("MessageScreen EFFECT: No valid currentChannelId, listeners not set.");
        return; // Exit early
    }

    //console.log(`MessageScreen EFFECT RUNNING for channel: ${currentChannelId}`);

    const handleChannelMessages = (channel_messages) => {   
      console.log(`📨 handleChannelMessages called for channel ${currentChannelId}:`, {
        receivedType: typeof channel_messages,
        isArray: Array.isArray(channel_messages),
        count: Array.isArray(channel_messages) ? channel_messages.length : 0
      });
      const messagesToShow = Array.isArray(channel_messages) ? channel_messages : [];
      setMessages(messagesToShow); 

      if (messagesToShow.length > 0) {
        // Find an incoming message to get the customer's userId for SET_ACCOUNT_NO
        // Only update if we don't already have a valid account number (prevents race conditions)
        const incomingMessageForAccountNo = messagesToShow.find(msg => msg.direction === 0 && msg.userId);
        if (incomingMessageForAccountNo && (!data.accountNo || data.accountNo === 0)) {
          console.log(`Setting account number from messages: ${incomingMessageForAccountNo.userId}`);
          dispatch({ type: "SET_ACCOUNT_NO", payload: incomingMessageForAccountNo.userId });
        } else if (incomingMessageForAccountNo) {
          console.log(`Account number already set (${data.accountNo}), not overriding with message userId (${incomingMessageForAccountNo.userId})`);
        }

        // Use the name from the first message for SET_CUSTOMER, or a fallback
        // This could also be from an incoming message if preferred.
        const messageForCustomerName = messagesToShow[0]; // Or find an incoming one specifically
        if (messageForCustomerName && typeof messageForCustomerName.name === 'string' && messageForCustomerName.name.trim() !== '') {
          dispatch({ type: "SET_CUSTOMER", payload: messageForCustomerName.name });
        }

        let last = messagesToShow[messagesToShow.length - 1];
        if (last && typeof last === 'object' && last.status === 0) { 
          socket.emit("read-message", last);   
        }
      }

      // If no account number was set from messages, use the channel_id as account number
      if (!data.accountNo && currentChannelId && currentChannelId !== 0) {
        console.log(`Setting account number from channel_id: ${currentChannelId}`);
        dispatch({ type: "SET_ACCOUNT_NO", payload: currentChannelId });
        // Also trigger the loading state to fetch customer data
        dispatch({ type: "START_ACCOUNT_DATA_LOADING" });
      }
    };

    const handleMessagesRead = (message) => {  
      setMessages(prevMessages => {
        if (Array.isArray(prevMessages) && message && message.channel_id !== undefined) {
          return prevMessages.map((item) =>
            (item && item.channel_id === message.channel_id)
              ? { ...item, state: message.state }
              : item
          );
        }
        return prevMessages;
      });
    };

    // Note: receive-message is now handled at top level (Chat/index.jsx)
    // and dispatched through context, so we don't register it here anymore
    
    const handleSocketConnect = () => {
      console.log("🔄 Socket connected, retrying channel messages request");
      if (currentChannelId && currentChannelId !== 0) {
        AsyncLocalStorage.getItem("silu-ClientId").then(clientId => {
          if (clientId) {
            console.log(`🔄 Retrying set-channel after connection: {clientId: ${clientId}, channelId: ${currentChannelId}}`);
            socket.emit("set-channel", {clientId: clientId, channelId: currentChannelId});
          }
        });
      }
    };
    
    socket.on("channel-messages", handleChannelMessages);
    socket.on("messages-read", handleMessagesRead);
    socket.on("connect", handleSocketConnect);

    return () => { 
      //console.log(`MessageScreen CLEANUP for channel: ${currentChannelId}`);
      socket.off("channel-messages", handleChannelMessages);
      socket.off("messages-read", handleMessagesRead);
      socket.off("connect", handleSocketConnect); 
      // Note: receive-message is managed at top level, not here
    };
 
 
  }, [data.currentChat?.channel_id, dispatch]);

  // Moved getData and its triggering useEffect from Home.jsx
  const getData = useCallback(async (account_number, userNameForComment) => {
    let nav = [{ name: "Home", action: "SHOW_ADDRESS", icon: HomeIcon }];

    const date = new Date();
    let dd = String(date.getDate()).padStart(2, '0');
    let mm = String(date.getMonth() + 1).padStart(2, '0'); 
    let yyyy = date.getFullYear();
    let today = yyyy + "-" + mm + "-" + dd;

    let customer_data = {};

    const baseAccountDataStructure = {
      "account": {},
      "balance": 0,
      "balance_data": {},
      "comments": [],
      "connection_status": 0,
      "location": [], 
      "transactions": []
    };

    try {
      // Use edamsApi instead of direct axios calls
      const response = await edamsApi.getAccountDetails(account_number);
      if (response.status == 1) { 
        customer_data['account'] = response.account;

        // Get balance info
        const balanceRes = await edamsApi.getBalanceInfo(account_number, today);
        if (balanceRes.status == 1) {
          nav.push({ name: "Transactions", action: "SHOW_TRANSACTIONS", icon: CreditCardIcon });
          customer_data['balance_data'] = balanceRes;
          customer_data['balance'] = parseFloat(balanceRes.last_statement.CL_BLNCE).toFixed(2);  
          
          // Get transactions
          const transactionsResponse = await edamsApi.getAccountTransactions(account_number);
          if(transactionsResponse.status == 1) {   
            let transactions = [];
            let bal = null;
            transactionsResponse.transactions.forEach((item, index) =>{ 
              const type = item.trns_type < 50 ? 'Credit' : 'Debit';
              const vat = (item.vat_amount == "NULL" || item.vat_amount == undefined || item.vat_amount == '') ? '' : 'R'+parseFloat(item.vat_amount).toFixed(2);
              
              if(index === 0) {                              
                bal = parseFloat(item.OP_BLNCE) + parseFloat(item.amount);
              } else {
                bal = parseFloat(bal) + parseFloat(item.amount); 
              }   
              const balance = 'R'+parseFloat(bal).toFixed(2);                                          
              const effect_date = item.effect_dte.substring(0, 10);               
              const amount = 'R'+parseFloat(item.amount).toFixed(2);  
              transactions.push({
                No : index + 1, 
                Date :effect_date, 
                Type: type,
                Description: item.trns_stitl,
                Amount: amount,
                Vat: vat,
                Balance: balance
              }); 
            });
            customer_data['transactions'] = transactions;
          }
        }

        // Get comments
        const commentsRes = await edamsApi.getAccountComments(account_number, userNameForComment === "System" ? "Billy Metiso" : userNameForComment);
        if (commentsRes.status == 1) {
          nav.push({ name: "Comments", action: "SHOW_COMMENTS", icon: PencilSquareIcon });
        }
        if (commentsRes.comments) {
            customer_data['comments'] = commentsRes.comments.reverse();  
        }
        if (commentsRes.user && typeof commentsRes.user === 'object') {
          customer_data['edams_user'] = commentsRes.user.USER_NAME; 
        }

        // Get account status
        const statusRes = await edamsApi.getAccountStatus(account_number);
        if (statusRes.status == 1) {
          nav.push({ name: "Connection", action: "SHOW_STATUS", icon: InformationCircleIcon });
          if (statusRes.account_status?.CON_STATUS == 1) {
            customer_data['connection_status'] = 1;
          } else if (statusRes.account_status?.CON_STATUS == 2) {
            customer_data['connection_status'] = 2;
          }
        }
      }
      dispatch({ 
        type: "SET_ACCOUNT_DATA", 
        payload: { 
          fetchedData: { ...baseAccountDataStructure, ...customer_data },
          fetchedForAccountNo: account_number
        } 
      });
      dispatch({ type: "SET_NAVIGATION", payload: nav }); 
    } catch (error) {
      console.error("Error in getData (MessageScreen):", error);
      dispatch({ type: "FINISH_ACCOUNT_LOADING_WITHOUT_DATA" });
    }
  }, [dispatch]);

  useEffect(() => {
    const userNameForComment = data.user?.name; // Use user name from context
    console.log(
      `MessageScreen DATA_FETCH_EFFECT: accountNo=${data.accountNo}, isAccountDataLoading=${data.isAccountDataLoading}, userName=${userNameForComment}, data.user exists=${!!data.user}, hasAccountData=${!!data.accountData?.account}, hasNavigation=${!!data.navigation && data.navigation.length > 0}`
    );

    // Check if we need to load data: loading flag is true OR we have an account number but no data
    const needsDataLoad = data.isAccountDataLoading || 
                          (data.accountNo && data.accountNo.toString().startsWith('921') && 
                           (!data.accountData?.account || Object.keys(data.accountData.account).length === 0));

    if (!needsDataLoad) {
      console.log("MessageScreen DATA_FETCH_EFFECT: Data already loaded or not needed. No action.");
      return;
    }
    
    if (!data.isAccountDataLoading && data.accountNo && data.accountNo.toString().startsWith('921')) {
      console.log("MessageScreen DATA_FETCH_EFFECT: Account data missing but loading flag is false. Triggering load.");
      dispatch({ type: "START_ACCOUNT_DATA_LOADING" });
      return;
    }

    // At this point, data.isAccountDataLoading is TRUE.
    console.log("MessageScreen DATA_FETCH_EFFECT: `data.isAccountDataLoading` is TRUE.");

    // Check if all necessary data is available to proceed with fetching
    if (data.accountNo != null && data.accountNo !== 0 && userNameForComment && userNameForComment !== "") {
      console.log(`MessageScreen DATA_FETCH_EFFECT: All prerequisites MET (accountNo, userName). accountNo: ${data.accountNo}`);
      const accountNoStr = data.accountNo.toString();
      // Check if account number is valid (at least 7 digits) and starts with "921"
      const isValidAccountNo = accountNoStr.length >= 7;
      const shouldFetchAccountData = isValidAccountNo && accountNoStr.startsWith("921");
      
      if (shouldFetchAccountData) {
        console.log(`MessageScreen DATA_FETCH_EFFECT: ==> Eligible ("921") and calling getData for account: ${data.accountNo}`);
        getData(data.accountNo, userNameForComment);
        // getData will dispatch SET_ACCOUNT_DATA which sets isAccountDataLoading = false
      } else if (!isValidAccountNo) {
        console.log(`MessageScreen DATA_FETCH_EFFECT: AccountNo (${data.accountNo}) is too short (${accountNoStr.length} digits). Waiting for full account number.`);
        // Don't finish loading yet - wait for the real account number
      } else {
        console.log(`MessageScreen DATA_FETCH_EFFECT: AccountNo (${data.accountNo}) NOT eligible (doesn't start with 921). Dispatching FINISH_ACCOUNT_LOADING_WITHOUT_DATA.`);
        dispatch({ type: "FINISH_ACCOUNT_LOADING_WITHOUT_DATA" });
      }
    } else {
      // Prerequisites for fetching are not met (accountNo or userName is missing/invalid).
      console.log(`MessageScreen DATA_FETCH_EFFECT: Prerequisites NOT MET. accountNo=${data.accountNo}, userName=${userNameForComment}.`);
      
      // If accountNo is explicitly null/undefined/0 (not just missing), finish loading immediately
      if (data.accountNo == null || data.accountNo === 0) {
        console.log(`MessageScreen DATA_FETCH_EFFECT: AccountNo is ${data.accountNo}. Dispatching FINISH_ACCOUNT_LOADING_WITHOUT_DATA immediately.`);
        dispatch({ type: "FINISH_ACCOUNT_LOADING_WITHOUT_DATA" });
      }
      // If userName is missing, wait for it to be set (effect will re-run)
    }
  }, [data.accountNo, data.user, data.isAccountDataLoading, getData, dispatch]);

   const HandleSubmit = async (event) => {
    event.preventDefault();
   if (text.trim()) {     
      console.log("💬 HandleSubmit - Sending message...", {
        currentChat,
        user,
        text: text.substring(0, 50),
        socketConnected: socket.connected
      });

      // Check if socket is connected
      if (!socket.connected) {
        console.error("❌ Socket is not connected! Cannot send message.");
        alert("Connection lost. Please refresh the page.");
        return;
      }

      socket.emit("set-channel", currentChat.channel_id); 
      const message = {
        timestamp: Math.floor(Date.now() / 1000), 
        userId: user.user_id,
        name: user.user_full_name || user.name,
        message: text,
        channelId: currentChat.channel_id, 
        direction: 1,
        category: currentChat.category, 
        department: department,
        id: uuidv4()
      }   

      console.log("📤 Emitting send-message:", message);
      socket.emit("send-message", message);
      
      // OPTIMISTIC UPDATE: Add message to local state immediately
      setMessages(prevMessages => [...prevMessages, {
        ...message,
        channel_id: currentChat.channel_id,
        state: 'sending' // Indicates message is being sent
      }]);
      console.log("✅ Message added to local state optimistically");
      
      // Reset states after sending
      setText("");
      setIsTyping(false);

      if (preview) { // If the message sent was an image from preview
        setFile(null);      // Clear the file state (used for <img src={file}/>)
        setPreview(false);  // Hide the preview UI
        if (inputFile.current) {
          //console.log("HandleSubmit: Resetting inputFile.current.value from:", inputFile.current.value);
          inputFile.current.value = null; // CRITICAL: Reset the actual file input
        }
      }
      //socket.emit("stop-typing"); 
    } 
  };


    const handleFile = async (event) => {
      const selectedFile = event.target.files && event.target.files[0];
      //console.log("handleFile: event.target.files:", event.target.files, "selectedFile:", selectedFile);

      if (selectedFile) { // Check if a file was actually selected
        const fileName = selectedFile.name.toLowerCase(); // Work with lowercase for easier checks
        if (fileName.endsWith(".png") || // Use endsWith for more precise type checking
            fileName.endsWith(".jpeg") ||
            fileName.endsWith(".jpg") ||
            fileName.endsWith(".gif")) {

          try {
            const resizedImage = await resizeImage(selectedFile, 600, 600, 0.8);
            setText(resizedImage); // Set text to base64 for sending
            setFile(resizedImage); // Set file for preview
            setPreview(true);
          } catch (err) {
            console.error("Error resizing image:", err);
            alert("Error processing image.");
            if (inputFile.current) { // Reset input on error
                //console.log("handleFile (resize error): Resetting inputFile.current.value from:", inputFile.current.value);
                inputFile.current.value = null;
            }
          }
        } else {
          alert("Allowed File Types are: JPEG, PNG, GIF, JPG");
          if (inputFile.current) { // Reset input if file type is not allowed
            //console.log("handleFile (invalid type): Resetting inputFile.current.value from:", inputFile.current.value);
            inputFile.current.value = null;
          }
        }
      } else {
        //console.log("File selection cancelled or no file selected.");
        // Explicitly reset the input if the dialog was cancelled
        if (inputFile.current) {
            //console.log("handleFile (no file selected/cancelled): Resetting inputFile.current.value from:", inputFile.current.value);
            inputFile.current.value = null;
        }
      }
    }

    const onFileClick = () => { 
      //console.log("onFileClick: Triggering click on inputFile.current. Current value:", inputFile.current?.value);
      // Ensure it's reset *before* click, just in case.
      if (inputFile.current) inputFile.current.value = null; 
      inputFile.current.click();
    };  
 
 
  return (
    <section
      aria-labelledby="primary-heading"
      className="sm:flex h-screen min-w-0 flex-1 flex-col overflow-hidden"
    >
      <div className="flex flex-col h-full">
        <div className="relative flex h-full w-full">
          {/* Main content area (messages and input form) */}
          <div className="flex flex-col flex-grow h-full w-full">
            {/* Chat Header - Fixed height */}
            <div className={`h-16 flex items-center px-6 py-2 border-b border-gray-200 bg-white shadow-sm flex-shrink-0 relative z-20 ${isSidebarVisible ? 'pr-[25rem]' : ''}`}>
              <div className="flex items-center justify-between w-full">
                {/* Left Section: Customer Info */}
                <div className="flex flex-col">
                  <h2 className="text-lg font-semibold text-gray-800 leading-tight">
                    {data.currentChat?.customer_name || customer || 'Unknown Customer'}
                  </h2>
                  <p className="text-sm text-gray-500 leading-tight">
                    Account No: {data.currentChat?.account_no || "N/A"}
                  </p>
                </div>
                {/* Right Section: Agent Info and Sidebar Toggle */}
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-600">{user?.user_full_name || "Your Name"}</span>
                  {/* Search Customer Button - Hidden when sidebar is visible */}
                  {!isSidebarVisible && (
                    <button
                      onClick={() => setIsSearchModalOpen(true)}
                      className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                      title="Search Customer"
                    >
                      <MagnifyingGlassIcon className="h-5 w-5" />
                    </button>
                  )}
                  {/* Sidebar Toggle Button */}
                  <button
                    onClick={() => {
                      console.log('Toggle sidebar clicked, current state:', isSidebarVisible);
                      setIsSidebarVisible(!isSidebarVisible);
                    }}
                    className={`relative p-2 rounded-lg transition-colors ${
                      hasCustomerData 
                        ? 'bg-green-100 hover:bg-green-200 text-green-600' 
                        : 'bg-sky-100 hover:bg-sky-200 text-sky-600'
                    }`}
                    title={
                      hasCustomerData 
                        ? (isSidebarVisible ? "Hide customer details" : "Customer details available") 
                        : (isSidebarVisible ? "Hide sidebar" : "Show sidebar")
                    }
                  >
                    {hasCustomerData && !isSidebarVisible && (
                      <span className="absolute -top-1 -right-1 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                      </span>
                    )}
                    {isSidebarVisible ? (
                      <ChevronRightIcon className="h-5 w-5" />
                    ) : (
                      <ChevronLeftIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex flex-col flex-grow w-full py-5 px-5 overflow-y-auto bg-gray-50" data-messages-container>
              {Array.isArray(messages) && messages.map((message, id) => ( 
                <Message key={id} message={message} /> 
              ))}
            </div>
            {/* Input form area */}
            <form onSubmit={HandleSubmit} className="w-full flex items-center gap-3 px-4 py-3 border-t border-gray-200 bg-white flex-shrink-0">
              { preview ? 
              <div className="flex flex-col items-center w-full p-2 border border-gray-200 rounded-lg bg-gray-50">
                <img src={file} alt="Preview" className="max-h-40 rounded-md mb-2 object-contain"/>
                <div className='flex gap-2'>
                  <IconButton 
                    type="submit"
                    className="!bg-emerald-500 hover:!bg-emerald-600 text-white rounded-lg p-2 shadow hover:shadow-md transition-all"
                  >
                      <PaperAirplaneIcon className="h-6 w-6 text-white" />
                  </IconButton> 
                  <IconButton 
                    onClick={() => { 
                      setFile(null); setText(""); setPreview(false); if(inputFile.current) inputFile.current.value = null; 
                    }}
                    className="!bg-gray-300 hover:!bg-gray-400 text-gray-700 rounded-lg p-2 shadow hover:shadow-md transition-all"
                  > 
                     <XMarkIcon className="h-6 w-6 text-gray-700 hover:text-red-600"/>
                  </IconButton>
                </div>
              </div> : <>
              <input type='file' id='file' ref={inputFile} style={{display: 'none'}} onChange={handleFile}/> 
              <button
                type="button"
                onClick={onFileClick} 
                className="p-2 rounded-full bg-sky-500 hover:bg-sky-600 text-white transition-colors"
              >
                <PaperClipIcon className="h-6 w-6" />
              </button>
                  <input
                    type="text"
                    placeholder="Type here"
                    className="flex-grow p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition-shadow"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                  <IconButton 
                    type="submit"
                    disabled={!text.trim()} 
                    className={`transition-colors ${text.trim() ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-gray-300 cursor-not-allowed'}`}  
                  >
                    <PaperAirplaneIcon className={`h-6 w-6 ${text.trim() ? 'text-white' : 'text-gray-500'}`} />
                  </IconButton>
                  </>
                   }
            </form>
          </div>
          {/* Sidebar area - rendered conditionally based on toggle */}
          {console.log('Sidebar visibility state:', isSidebarVisible)}
          {console.log('Navigation array:', navigation)}
          {isSidebarVisible && (
            <div className="absolute right-0 top-0 flex flex-col w-96 h-full bg-white shadow-2xl border-l border-gray-200 z-30">
                {console.log('Sidebar is rendering')}
                {/* Top navigation icon bar */}
                <nav
                  aria-label="Sidebar Navigation"
                  className="h-16 flex flex-row justify-around items-center px-4 bg-gray-50 shadow-sm border-b border-gray-200 flex-shrink-0"
                >
                  {/* Search Icon */}
                  <div
                    onClick={() => setIsSearchModalOpen(true)}
                    title="Search Customer"
                    className="flex items-center justify-center p-2 rounded-md cursor-pointer transition-colors duration-150 group hover:bg-sky-100"
                  >
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-500 group-hover:text-sky-600" aria-hidden="true" />
                  </div>
       
                  {navigation && navigation.map((item) => (
                    <div 
                      onClick={() => dispatch({ type: "SET_TAB", payload: item.name })}
                      key={item.name}
                      title={item.name}
                      className={`flex items-center justify-center p-2 rounded-md cursor-pointer transition-colors duration-150 group
                                  ${item.name === data.tab ? "bg-sky-500" : "bg-transparent hover:bg-sky-500"}`}
                    >
                      <item.icon
                        className={`h-5 w-5 transition-colors duration-150 
                                    ${item.name === data.tab ? "text-white" : "text-gray-500 group-hover:text-white"}`}
                        aria-hidden="true"
                      />
                      <span className="sr-only">{item.name}</span>
                    </div>
                  ))}
                </nav> 
                <div className="flex-grow overflow-y-auto">
                    <SideChatAccount key={data.accountNo || 'initial'} />
                </div>
            </div>
          )}
            <CustomerSearchModal 
              isOpen={isSearchModalOpen} 
              onClose={() => setIsSearchModalOpen(false)} 
            />
        </div>
      </div>
    </section>
  );
};

export default MessagesDashboard;
